import { useState, useMemo } from 'react';
import { Archive, BookOpen, BarChart2, ArrowRight, Loader2, Target, CheckCircle, Clock, Calendar } from 'lucide-react';
import { Link } from 'wouter';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { listChatSessions, getSavedMessages, updateSavedMessageMetadata } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import AppLayout from '../components/AppLayout';
import type { SessionSummary, SavedMessage } from '../types/session';
import { useToast } from '../hooks/useToast';

const formatMinutes = (mins: number) => {
  if (mins <= 0) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

const formatDate = (dateString: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const InsightsHubPage = () => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const { getToken, isSignedIn } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();

  const resources = [
    {
      id: 'mom-archive',
      title: 'MoM Archive',
      description: 'Access all your past coaching session minutes and key takeaways',
      icon: Archive,
      path: '/archive',
      color: 'bg-emerald-100 text-emerald-700',
      hoverColor: 'hover:bg-emerald-50 hover:border-emerald-200',
    },
    {
      id: 'action-ledger',
      title: 'Action Ledger',
      description: 'Track and manage your action items and commitments',
      icon: BookOpen,
      path: '/wishlist',
      color: 'bg-blue-100 text-blue-700',
      hoverColor: 'hover:bg-blue-50 hover:border-blue-200',
    },
    {
      id: 'insights',
      title: 'Insights',
      description: 'Visualize your progress and discover patterns in your coaching journey',
      icon: BarChart2,
      path: '/insights',
      color: 'bg-purple-100 text-purple-700',
      hoverColor: 'hover:bg-purple-50 hover:border-purple-200',
    },
  ];

  const { data: sessions = [], isLoading: isLoadingSessions } = useQuery<SessionSummary[]>({
    queryKey: ['sessions-archive'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) return [];
      return listChatSessions(token);
    }
  });

  const { data: allSavedMessages = [], isLoading: isLoadingMessages } = useQuery<SavedMessage[]>({
    queryKey: ['savedMessages'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) return [];
      return getSavedMessages(token);
    }
  });

  const updateMetadataMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const token = await getToken();
      if (!token) throw new Error('No token');
      return updateSavedMessageMetadata({ id, ...updates }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedMessages'] });
      toastSuccess('Updated', 'Action item marked as Current Focus.');
    },
    onError: () => {
      toastError('Error', 'Failed to update focus.');
    }
  });

  const handleSetFocus = (id: string, isCurrentlyFocus: boolean) => {
    updateMetadataMutation.mutate({ 
      id, 
      updates: { 
        priority: isCurrentlyFocus ? 'medium' : 'high', 
        timeframe: isCurrentlyFocus ? '' : 'this week'
      } 
    });
  };

  const latestMoM = useMemo(() => {
    return (sessions || []).find(s => {
      if (s.mom_data) {
        if (typeof s.mom_data === 'string') {
          return s.mom_data.trim() !== '' && s.mom_data !== '{}' && s.mom_data !== 'null';
        } else if (typeof s.mom_data === 'object' && s.mom_data !== null) {
          return Object.keys(s.mom_data).length > 0;
        }
      }
      return false;
    });
  }, [sessions]);

  const parsedLatestMoM = useMemo(() => {
    if (!latestMoM?.mom_data) return null;
    let data = latestMoM.mom_data as any;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { return null; }
    }
    return data;
  }, [latestMoM]);

  const pendingActions = useMemo(() => {
    return allSavedMessages.filter(msg => (msg.status as any) !== 'done');
  }, [allSavedMessages]);

  return (
    <AppLayout currentPage="insights-hub">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {/* Headers */}
          <div>
            <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">
              Insights Hub
            </h1>
            <p className="mt-2 text-lg text-slate-600 max-w-3xl">
              Your central resource center for coaching insights, action items, and session archives
            </p>
          </div>

          {/* Resource Card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {resources.map((resource) => (
              <Link 
                key={resource.id}
                href={resource.path}
                className={`flex flex-col h-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 ${resource.hoverColor}`}
              >
                <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg ${resource.color}`}>
                  <resource.icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                  {resource.title}
                </h3>
                <p className="text-slate-600 text-sm mb-6 flex-grow">
                  {resource.description}
                </p>
                <div className="mt-auto flex items-center text-sm font-medium text-primary">
                  <span>View {resource.title}</span>
                  <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>

          {/* Recent Activity Section */}
          <div className="mt-12">
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-6">
              Recent Activity
            </h2>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex border-b border-slate-200 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('mom')}
                  className={`px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'mom' || activeTab === null
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Latest MoM
                </button>
                <button
                  onClick={() => setActiveTab('actions')}
                  className={`px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'actions'
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Pending Actions
                </button>
                <button
                  onClick={() => setActiveTab('insights')}
                  className={`px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'insights'
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Key Insights
                </button>
              </div>
              <div className="p-6">
                {(activeTab === 'mom' || activeTab === null) && (
                  <div className="space-y-4">
                    {isLoadingSessions ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : parsedLatestMoM ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                          <Calendar className="h-5 w-5 text-primary" />
                          <h3 className="font-display text-lg font-bold text-slate-900">
                            Session from {formatDate(latestMoM?.updated_at || latestMoM?.created_at || '')}
                          </h3>
                        </div>
                        <div className="space-y-4">
                          {parsedLatestMoM.insights_hub_report ? (
                            <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">
                              {parsedLatestMoM.insights_hub_report}
                            </div>
                          ) : parsedLatestMoM.observationalSummary ? (
                            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                              {parsedLatestMoM.observationalSummary}
                            </p>
                          ) : parsedLatestMoM.keyTakeaways && Array.isArray(parsedLatestMoM.keyTakeaways) ? (
                            <ul className="space-y-2">
                              {parsedLatestMoM.keyTakeaways.slice(0, 3).map((takeaway: string, idx: number) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                                  <span>{takeaway}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-slate-600">Summary available in archive.</p>
                          )}
                        </div>
                        <div className="mt-6">
                          <Link href="/archive" className="inline-flex items-center text-sm font-medium text-primary hover:underline">
                            <span>View full Archive</span>
                            <ArrowRight className="ml-1 h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-sm text-slate-600 mb-2">No coaching session summaries found.</p>
                        <Link href="/coaching" className="inline-flex items-center text-sm font-medium text-primary hover:underline">
                          <span>Start a session</span>
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'actions' && (
                  <div className="space-y-4">
                    {isLoadingMessages ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : pendingActions.length > 0 ? (
                      <div className="space-y-3">
                        {pendingActions.map(action => {
                          const isCurrentFocus = action.priority === 'high' && action.timeframe === 'this week';
                          return (
                            <div key={action.id} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border ${isCurrentFocus ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white'}`}>
                              <div className="flex items-start gap-3">
                                {action.status === 'in_progress' ? (
                                  <Clock className={`h-5 w-5 mt-0.5 shrink-0 ${isCurrentFocus ? 'text-primary' : 'text-slate-400'}`} />
                                ) : (
                                  <Target className={`h-5 w-5 mt-0.5 shrink-0 ${isCurrentFocus ? 'text-primary' : 'text-slate-400'}`} />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{action.messageContent}</p>
                                  <p className="text-xs text-slate-500 mt-1 capitalize">Status: {(action.status as string).replace('_', ' ')}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleSetFocus(action.id, isCurrentFocus)}
                                className={`shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                  isCurrentFocus 
                                    ? 'bg-primary text-white hover:bg-primary/90' 
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                {isCurrentFocus ? 'Current Focus' : 'Set as Current Focus'}
                              </button>
                            </div>
                          );
                        })}
                        <div className="pt-4 border-t border-slate-100">
                          <Link href="/wishlist" className="inline-flex items-center text-sm font-medium text-primary hover:underline">
                            <span>Manage all actions</span>
                            <ArrowRight className="ml-1 h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-sm text-slate-600 mb-2">No pending action items found.</p>
                        <Link href="/wishlist" className="inline-flex items-center text-sm font-medium text-primary hover:underline">
                          <span>Go to Action Ledger</span>
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'insights' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">Your key coaching insights will appear here.</p>
                    <Link href="/insights" className="inline-flex items-center text-sm font-medium text-primary hover:underline">
                      <span>View all insights</span>
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default InsightsHubPage;

